<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-700">
        {{ phoneNumbers.length }} phone number{{ phoneNumbers.length !== 1 ? 's' : '' }}
      </h3>
      <button
        class="text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="fetchData"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="mr-1" />
        Refresh
      </button>
    </div>

    <p data-test="support-banner" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
      To reassign, unassign, deactivate or delete a phone number, email
      <a href="mailto:support@kaironhealth.com" class="underline">support@kaironhealth.com</a>.
    </p>

    <!-- Approved area codes: tenant admins, security and superusers edit them. -->
    <div data-test="area-codes" class="mb-4 p-4 border border-gray-200 rounded-lg">
      <div class="text-xs font-medium text-gray-700 mb-2">Approved area codes</div>
      <div class="flex flex-wrap gap-2 items-center">
        <span
          v-for="code in approvedAreaCodes"
          :key="code"
          data-test="area-code-chip"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
        >
          {{ code }}
          <button
            v-if="canManage"
            data-test="remove-area-code"
            class="ml-1 text-gray-500 hover:text-gray-800 disabled:opacity-50"
            :aria-label="`Remove ${code}`"
            :disabled="savingConfig"
            @click="removeAreaCode(code)"
          >
            ×
          </button>
        </span>
        <span v-if="!approvedAreaCodes.length" class="text-xs text-gray-500">
          None yet.{{ canManage ? ' Add the area codes your organization can buy numbers in.' : '' }}
        </span>
      </div>

      <div v-if="canManage" data-test="area-code-editor" class="mt-3 flex gap-2 items-center">
        <input
          v-model="newAreaCode"
          data-test="new-area-code"
          type="text"
          maxlength="3"
          placeholder="312"
          aria-label="Area code to add"
          class="text-sm border border-gray-300 rounded-md px-2 py-1 w-20"
          :disabled="savingConfig"
          @keydown.enter.prevent="addAreaCode"
        >
        <button
          data-test="add-area-code"
          class="text-sm px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          :disabled="savingConfig || !newAreaCode.trim()"
          @click="addAreaCode"
        >
          Add
        </button>
        <span v-if="savingConfig" data-test="area-codes-saving" class="text-xs text-gray-500">Saving…</span>
      </div>
      <ul v-if="areaCodeErrors.length" data-test="area-code-errors" class="mt-2 text-xs text-red-700 list-disc ml-4">
        <li v-for="e in areaCodeErrors" :key="`${e.code}-${e.reason}`">
          {{ e.code }}: {{ e.reason === 'duplicate' ? 'listed more than once' : 'not a valid US area code' }}
        </li>
      </ul>
    </div>

    <!-- Purchase: tenant admins/security and superusers. -->
    <div v-if="canManage" data-test="purchase-panel" class="mb-4 p-4 border border-gray-200 rounded-lg space-y-3">
      <p
        v-if="!purchaseEnabled"
        data-test="purchase-disabled-banner"
        class="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800"
      >
        Purchasing phone numbers is disabled in this environment. It is only available in production.
      </p>

      <div v-if="!addOpen">
        <button
          data-test="add-number"
          class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
          :disabled="approvedAreaCodes.length === 0"
          @click="openAdd"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
          Add number
        </button>
        <span v-if="approvedAreaCodes.length === 0" class="ml-2 text-xs text-gray-500">
          No approved area codes yet.
        </span>
      </div>

      <div v-else data-test="add-form" class="space-y-3">
        <div class="flex gap-2 items-end">
          <div>
            <label for="add-area-code" class="block text-xs font-medium text-gray-700 mb-1">Area code</label>
            <select id="add-area-code" v-model="search.areaCode" class="text-sm border border-gray-300 rounded-md px-2 py-1">
              <option v-for="code in approvedAreaCodes" :key="code" :value="code">{{ code }}</option>
            </select>
          </div>
          <button
            data-test="search-numbers"
            class="text-sm px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
            :disabled="search.loading"
            @click="runSearch"
          >
            Search
          </button>
        </div>

        <p v-if="search.notice" data-test="search-notice" class="text-xs text-orange-700">{{ search.notice }}</p>

        <div v-if="search.results.length">
          <label for="add-number-choice" class="block text-xs font-medium text-gray-700 mb-1">Number</label>
          <select id="add-number-choice" v-model="purchase.phoneNumber" class="text-sm border border-gray-300 rounded-md px-2 py-1 w-full">
            <option v-for="n in search.results" :key="n.phone_number" :value="n.phone_number">
              {{ formatPhone(n.phone_number) }}{{ placeLabel(n) ? ` — ${placeLabel(n)}` : '' }}
            </option>
          </select>
        </div>
        <p v-else-if="search.done && search.searchedCode" data-test="search-empty" class="text-xs text-gray-600">
          Twilio has no numbers available in area code {{ search.searchedCode }} right now. This is common in busy
          area codes; try another approved area code, or try again later.
        </p>

        <div v-if="search.results.length" data-test="assignee-picker">
          <FilterDropdown
            v-model="purchase.userId"
            label="Assign to"
            :options="userOptions"
            placeholder="Select a user"
            search-placeholder="Search users…"
            searchable
            sensitive-labels
            full-width
            class="w-full"
          />
        </div>

        <div class="flex gap-2">
          <button
            data-test="review-purchase"
            class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            :disabled="!purchase.phoneNumber || !purchase.userId || !purchaseEnabled"
            @click="confirmOpen = true"
          >
            Review purchase
          </button>
          <button class="text-sm px-3 py-1 rounded-md border border-gray-300" @click="closeAdd">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Confirmation: nothing is bought until the user confirms here. -->
    <div
      v-if="confirmOpen"
      data-test="confirm-dialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-purchase-title"
    >
      <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md space-y-3">
        <h4 id="confirm-purchase-title" class="text-base font-semibold text-gray-900">Buy this phone number?</h4>
        <dl class="text-sm grid grid-cols-3 gap-y-1">
          <dt class="text-gray-500">Number</dt>
          <dd data-test="confirm-number" class="col-span-2 sensitive">{{ formatPhone(purchase.phoneNumber) }}{{ selectedLocation }}</dd>
          <dt class="text-gray-500">Organization</dt>
          <dd data-test="confirm-tenant" class="col-span-2">{{ current_tenant_name }}</dd>
          <dt class="text-gray-500">Assigned to</dt>
          <dd data-test="confirm-assignee" class="col-span-2 sensitive">{{ selectedUserLabel }}</dd>
        </dl>
        <p class="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded p-2">
          This purchase costs money: the number is billed monthly to your organization's Twilio account.
        </p>
        <div class="flex justify-end gap-2">
          <button
            data-test="cancel-purchase"
            class="text-sm px-3 py-1 rounded-md border border-gray-300"
            :disabled="purchase.loading"
            @click="confirmOpen = false"
          >
            Cancel
          </button>
          <button
            data-test="confirm-purchase"
            class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            :disabled="purchase.loading"
            @click="confirmPurchase"
          >
            Buy number
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <div v-else-if="phoneNumbers.length === 0" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">No phone numbers configured</p>
    </div>

    <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <FilterTabs v-model="statusFilter" :options="statusFilterOptions" />
        <div class="flex flex-wrap items-center gap-3">
          <input
            v-model="searchQuery"
            data-test="phone-number-search"
            type="text"
            placeholder="Search number, name or user..."
            aria-label="Search phone numbers"
            class="w-full sm:w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
          <select
            v-model="assignmentFilter"
            data-test="assignment-filter"
            aria-label="Filter by assignment"
            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Any assignment</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table data-test="phone-numbers-table" class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap"
                :aria-sort="sortAria(col.key)"
                @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <font-awesome-icon
                  v-if="sortColumn === col.key"
                  :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                  class="ml-1"
                />
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="!sortedNumbers.length">
              <td :colspan="columns.length" data-test="no-matches" class="px-4 py-8 text-center text-sm text-gray-500">
                No phone numbers match the current search or filters.
              </td>
            </tr>
            <tr v-for="num in sortedNumbers" :key="num.id" data-test="phone-number-row" class="hover:bg-gray-50">
              <td class="px-4 py-2 text-sm text-gray-900 whitespace-nowrap tabular-nums">
                <span class="sensitive">{{ formatPhone(num.phone_number) }}</span>
              </td>
              <td class="px-4 py-2 text-sm text-gray-900" data-test="friendly-name-cell">
                <div v-if="editingId === num.id" class="flex items-center gap-2">
                  <input
                    ref="friendlyNameInput"
                    v-model="friendlyNameDraft"
                    data-test="friendly-name-input"
                    type="text"
                    maxlength="100"
                    aria-label="Friendly name"
                    class="w-48 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    :disabled="savingFriendlyName"
                    @keydown.enter.prevent="saveFriendlyName(num)"
                    @keydown.esc.prevent="cancelEditFriendlyName"
                  >
                  <button
                    data-test="save-friendly-name"
                    class="text-xs px-2 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                    :disabled="savingFriendlyName"
                    @click="saveFriendlyName(num)"
                  >
                    Save
                  </button>
                  <button
                    data-test="cancel-friendly-name"
                    class="text-xs px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
                    :disabled="savingFriendlyName"
                    @click="cancelEditFriendlyName"
                  >
                    Cancel
                  </button>
                </div>
                <div v-else class="flex items-center gap-2 group">
                  <span :class="num.friendly_name ? '' : 'text-gray-400'">{{ num.friendly_name || 'No name' }}</span>
                  <button
                    v-if="canManage"
                    data-test="edit-friendly-name"
                    class="text-gray-400 hover:text-primary-600"
                    :aria-label="`Edit friendly name for ${formatPhone(num.phone_number)}`"
                    @click="startEditFriendlyName(num)"
                  >
                    <font-awesome-icon :icon="['fas', 'pencil']" class="text-xs" />
                  </button>
                </div>
                <p v-if="friendlyNameError && friendlyNameErrorId === num.id" data-test="friendly-name-error" class="mt-1 text-xs text-red-600">
                  {{ friendlyNameError }}
                </p>
              </td>
              <td class="px-4 py-2 text-sm whitespace-nowrap">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="num.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
                >{{ num.status || 'unknown' }}</span>
              </td>
              <td class="px-4 py-2 text-sm whitespace-nowrap" :class="num.assigned_user_name ? 'text-gray-900 sensitive' : 'text-gray-400'">
                {{ num.assigned_user_name || 'Unassigned' }}
              </td>
              <td class="px-4 py-2 text-sm text-gray-900">{{ yesNo(num.voice_enabled) }}</td>
              <td class="px-4 py-2 text-sm text-gray-900">{{ yesNo(num.sms_enabled) }}</td>
              <td class="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{{ formatDate(num.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div data-test="showing-count" class="px-4 py-3 text-sm text-gray-500 border-t border-gray-100">
        Showing {{ sortedNumbers.length }} of {{ phoneNumbers.length }} numbers
      </div>
    </div>

    <div v-if="error" data-test="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'
import { mapGetters } from 'vuex'
import {
  listAdminPhoneNumbers,
  getTelephonyConfig,
  updateTelephonyConfig,
  searchAvailablePhoneNumbers,
  purchasePhoneNumber,
  telephonyError,
  updateFriendlyName,
} from '@/services/api/tenantPhoneNumbers'
import { listUsers } from '@/services/api/adminUsers'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'

export default {
  name: 'TelephonyPhoneNumbers_Admin',
  components: { FontAwesomeIcon, FilterDropdown, FilterTabs },

  data() {
    return {
      phoneNumbers: [],
      users: [],
      loading: true,
      error: null,
      approvedAreaCodes: [],
      purchaseEnabled: false,
      newAreaCode: '',
      areaCodeErrors: [],
      savingConfig: false,
      addOpen: false,
      confirmOpen: false,
      search: { areaCode: null, results: [], loading: false, done: false, notice: null, searchedCode: null },
      purchase: { phoneNumber: null, userId: null, loading: false },
      faSortUp,
      faSortDown,
      searchQuery: '',
      statusFilter: 'all',
      statusFilterOptions: [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      assignmentFilter: 'all',
      sortColumn: 'created_at',
      sortDirection: 'desc',
      columns: [
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'friendly_name', label: 'Friendly Name' },
        { key: 'status', label: 'Status' },
        { key: 'assigned_user_name', label: 'Assigned User' },
        { key: 'voice_enabled', label: 'Voice' },
        { key: 'sms_enabled', label: 'SMS' },
        { key: 'created_at', label: 'Added' },
      ],
      editingId: null,
      friendlyNameDraft: '',
      savingFriendlyName: false,
      friendlyNameError: null,
      friendlyNameErrorId: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_name', 'isTenantAdmin', 'isSecurityUser']),

    isSuperuser() {
      return Boolean(this.current_user?.is_superuser)
    },

    // Mirrors the backend's IsTenantAdminSecurityOrSuperuser; the API is the authority.
    canManage() {
      return this.isSuperuser || this.isTenantAdmin || this.isSecurityUser
    },

    userOptions() {
      return this.users.map((u) => ({
        label: `${u.first_name} ${u.last_name}`.trim() || u.email,
        value: u.id,
      }))
    },

    selectedUserLabel() {
      return this.userOptions.find((o) => o.value === this.purchase.userId)?.label || ''
    },

    selectedLocation() {
      const n = this.search.results.find((r) => r.phone_number === this.purchase.phoneNumber)
      const place = n ? this.placeLabel(n) : ''
      return place ? ` (${place})` : ''
    },

    filteredNumbers() {
      const query = this.searchQuery.trim().toLowerCase()
      const digits = query.replace(/\D/g, '')
      return this.phoneNumbers.filter((n) => {
        if (this.statusFilter !== 'all' && n.status !== this.statusFilter) return false
        if (this.assignmentFilter === 'assigned' && !n.assigned_user) return false
        if (this.assignmentFilter === 'unassigned' && n.assigned_user) return false
        if (!query) return true
        if (digits && (n.phone_number || '').replace(/\D/g, '').includes(digits)) return true
        return [n.friendly_name, n.assigned_user_name].some((v) => (v || '').toLowerCase().includes(query))
      })
    },

    sortedNumbers() {
      const key = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      const value = (n) => {
        const v = n[key]
        if (v === null || v === undefined) return ''
        return typeof v === 'string' ? v.toLowerCase() : v
      }
      return this.filteredNumbers.slice().sort((a, b) => {
        const av = value(a)
        const bv = value(b)
        if (av < bv) return -dir
        if (av > bv) return dir
        return 0
      })
    },
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    async fetchData() {
      this.loading = true
      this.error = null
      try {
        const [phones, users, config] = await Promise.all([
          listAdminPhoneNumbers(),
          this.canManage ? listUsers() : Promise.resolve([]),
          getTelephonyConfig(),
        ])
        this.phoneNumbers = phones
        this.users = users
        this.applyConfig(config)
      } catch {
        this.error = 'Failed to load phone numbers'
      } finally {
        this.loading = false
      }
    },

    applyConfig(config) {
      this.approvedAreaCodes = config.approved_area_codes || []
      this.purchaseEnabled = Boolean(config.purchase_enabled)
      this.areaCodeErrors = []
    },

    errorMessage(err, fallback) {
      return telephonyError(err)?.message || fallback
    },

    // Area codes save as soon as they are added or removed; the chips always show the saved list.
    async addAreaCode() {
      const code = this.newAreaCode.trim()
      if (!code || this.savingConfig) return
      // Duplicates and invalid codes are sent as-is so the API reports them per code.
      if (await this.saveAreaCodes([...this.approvedAreaCodes, code])) this.newAreaCode = ''
    },

    removeAreaCode(code) {
      if (this.savingConfig) return
      return this.saveAreaCodes(this.approvedAreaCodes.filter((c) => c !== code))
    },

    async saveAreaCodes(codes) {
      this.savingConfig = true
      this.error = null
      this.areaCodeErrors = []
      try {
        const config = await updateTelephonyConfig({ approved_area_codes: codes })
        this.applyConfig(config)
        return true
      } catch (err) {
        const apiErr = telephonyError(err)
        if (apiErr?.code === 'invalid_area_codes') {
          this.areaCodeErrors = apiErr.details || []
        } else {
          this.error = this.errorMessage(err, 'Failed to save approved area codes')
        }
        return false
      } finally {
        this.savingConfig = false
      }
    },

    openAdd() {
      this.addOpen = true
      this.search = {
        areaCode: this.approvedAreaCodes[0],
        results: [],
        loading: false,
        done: false,
        notice: null,
        searchedCode: null,
      }
      this.purchase = { phoneNumber: null, userId: null, loading: false }
    },

    closeAdd() {
      this.addOpen = false
      this.confirmOpen = false
    },

    async runSearch() {
      this.search.loading = true
      this.search.notice = null
      this.error = null
      try {
        this.search.searchedCode = this.search.areaCode
        this.search.results = await searchAvailablePhoneNumbers(this.search.areaCode)
        this.purchase.phoneNumber = this.search.results[0]?.phone_number || null
      } catch (err) {
        this.search.results = []
        this.search.searchedCode = null
        this.error = this.errorMessage(err, 'Failed to search for numbers')
      } finally {
        this.search.loading = false
        this.search.done = true
      }
    },

    async confirmPurchase() {
      this.purchase.loading = true
      this.error = null
      try {
        await purchasePhoneNumber({ phone_number: this.purchase.phoneNumber, user_id: this.purchase.userId })
        this.confirmOpen = false
        this.addOpen = false
        await this.fetchData()
      } catch (err) {
        this.confirmOpen = false
        if (telephonyError(err)?.code === 'number_unavailable') {
          this.search.results = []
          this.search.done = false
          this.purchase.phoneNumber = null
          this.search.notice = 'That number is no longer available. Search again to pick another.'
        } else {
          this.error = this.errorMessage(err, 'Failed to buy number')
        }
      } finally {
        this.purchase.loading = false
      }
    },

    // Twilio often omits locality and returns region "US"; fall back to the rate center when it reads
    // like a city name. Rate centers such as "CHICGOZN05" are carrier codes, not places, so skip them.
    placeLabel(n) {
      const rateCenterName = n.rate_center && !/\d/.test(n.rate_center) ? this.titleCase(n.rate_center) : ''
      const city = n.locality || rateCenterName
      const region = n.region && n.region !== 'US' ? n.region : ''
      return [city, region].filter(Boolean).join(', ')
    },

    titleCase(value) {
      if (!value) return ''
      return value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    },

    toggleSort(key) {
      if (this.sortColumn === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = key
        this.sortDirection = 'asc'
      }
    },

    sortAria(key) {
      if (this.sortColumn !== key) return 'none'
      return this.sortDirection === 'asc' ? 'ascending' : 'descending'
    },

    yesNo(value) {
      return value ? 'Yes' : 'No'
    },

    startEditFriendlyName(num) {
      this.editingId = num.id
      this.friendlyNameDraft = num.friendly_name || ''
      this.friendlyNameError = null
      this.$nextTick(() => {
        const input = this.$refs.friendlyNameInput
        ;(Array.isArray(input) ? input[0] : input)?.focus()
      })
    },

    cancelEditFriendlyName() {
      this.editingId = null
      this.friendlyNameDraft = ''
    },

    async saveFriendlyName(num) {
      const name = this.friendlyNameDraft.trim()
      if (name === (num.friendly_name || '')) {
        this.cancelEditFriendlyName()
        return
      }
      this.savingFriendlyName = true
      this.friendlyNameError = null
      try {
        const updated = await updateFriendlyName(num.id, name)
        num.friendly_name = updated.friendly_name
        this.cancelEditFriendlyName()
      } catch (err) {
        this.friendlyNameError = this.errorMessage(err, 'Could not save the friendly name. Try again.')
        this.friendlyNameErrorId = num.id
      } finally {
        this.savingFriendlyName = false
      }
    },

    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : ''
    },

    formatPhone(phone) {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
  },
}
</script>
